import type { ReactNode } from "react";
import { StatusBadge, StatusStepper, Timestamp } from "@/design-system/components/ui";
import type { ResolvedPhaseLadder } from "./phaseLadder";

// Small presentational pieces shared by the Buyer CRM detail pages. Extracted so
// a field reads the same on a buyer, a listing and a submission — three
// hand-rolled definition lists drift, and the one that drifts is whichever
// nobody is looking at.

export function DetailSection({
  title,
  action,
  children,
}: {
  title: string;
  /**
   * Optional header-right slot — an edit affordance, a count, a link.
   *
   * <p>Additive on purpose: absent, this renders EXACTLY what it rendered before
   * the prop existed (the heading keeps its own `mb-space-3`, so no consumer
   * shifts by a pixel). The per-section edit pencil is the first consumer; the
   * shape follows `QualificationsProfileTab`'s `ProfileSection`, which pairs a
   * `Button variant="text" size="sm"` with a Pencil icon and an
   * `aria-label={`Edit ${title}`}` — a bare icon in a header row is otherwise
   * unnamed to a screen reader.</p>
   *
   * <p>A ReactNode rather than `onEdit` + `canEdit`: gating is the caller's
   * business, and a section whose only permitted action is "none" should render
   * no header row at all rather than a disabled control. Passing `null` or
   * `false` gets today's rendering back.</p>
   */
  action?: ReactNode;
  children: ReactNode;
}) {
  /*
    🔴 Was `text-h4`, which is not in the type scale — the scale runs
    label / caption / small / body / lead / h3 / h2 / h1 / display. Tailwind
    emitted nothing for it, so every section heading on all three detail
    pages rendered at inherited BODY size and the cards had no visible
    hierarchy at all.

    `lead` is the step below `h3`, which is the slot the author was reaching
    for; the weight is explicit because at 1.125rem regular a heading is not
    distinguishable from the text under it, and an invisible heading is the
    defect this is fixing. Page-LEVEL section headings stay `text-h3`.
  */
  const headingClass = "font-display font-semibold text-lead text-onyx";

  return (
    <section className="border border-border-light rounded-md p-space-4 bg-white-pure">
      {action ? (
        // The space below the header moves from the heading to the ROW, so it
        // stays exactly `space-3` either way rather than doubling.
        //
        // 🔴 The row WRAPS, and the action slot may shrink. It used to be
        // `shrink-0` on a row that could not wrap, so an action wider than the
        // card (the Deal structure card's two buttons in the record drawer) was
        // painted past the card's right edge. A flex row wraps on each item's
        // max-content width, so a header whose title and action already fit on
        // one line renders exactly as before; only one that did not fit moves
        // its action beneath the title. `ml-auto` keeps a wrapped action on the
        // right, where it sits when it shares the line.
        <div className="flex flex-wrap items-center justify-between gap-space-3 mb-space-3">
          <h2 className={`${headingClass} min-w-0`}>{title}</h2>
          <div className="ml-auto min-w-0 max-w-full">{action}</div>
        </div>
      ) : (
        <h2 className={`${headingClass} mb-space-3`}>{title}</h2>
      )}
      {children}
    </section>
  );
}

/**
 * The condensed phase ladder — the chip rollup PLUS the raw status badge.
 *
 * <p>🔴 <b>Both, always.</b> A chip alone is a lossy read of a record: "Package"
 * does not say whether the package is drafted or has gone out, and "Won" does not
 * say whether a buyer converted or purchased. The badge is rendered from the same
 * resolved model rather than left to the caller, so the ladder cannot be mounted
 * without it.</p>
 *
 * <p>An off-ladder record still renders the whole ladder, unreached, with a note
 * beneath — the commission-claim timeline's treatment of Contested/Withdrawn.
 * The note is MUTED rather than a warning, matching the deliberate decision in
 * `buyerListBits` to paint every terminal status muted rather than red: closing a
 * buyer out is an ordinary, correct outcome, and a well-worked book should not
 * read as a wall of failures.</p>
 */
export function PhaseLadder({
  ladder,
  label = "Progress",
  "data-testid": testId,
}: {
  ladder: ResolvedPhaseLadder;
  label?: string;
  "data-testid"?: string;
}) {
  return (
    <div className="grid gap-space-2" data-testid={testId}>
      <div className="flex flex-wrap items-center gap-space-3">
        <StatusBadge tone={ladder.tone}>{ladder.statusLabel}</StatusBadge>
        <StatusStepper
          steps={ladder.steps}
          currentIndex={ladder.currentIndex}
          complete={ladder.complete}
          label={label}
        />
      </div>
      {ladder.offLadder && (
        <div
          className="p-space-3 border border-border-light rounded-md bg-white-warm text-small"
          data-testid="phase-ladder-off"
        >
          <span className="font-display text-label font-semibold uppercase tracking-label text-gray-mid">
            Off the ladder — {ladder.offLadder.label}
          </span>
          <div className="text-gray-mid mt-space-1">{ladder.offLadder.note}</div>
        </div>
      )}
    </div>
  );
}

/**
 * The label/value row now lives in the design system, alongside the list that
 * aligns a card's rows into one label column.
 *
 * <p>It was a buyers-module local with a FIXED `11rem` label track, which was
 * simultaneously too narrow for the two longest labels in the module and 30-75px
 * too wide for 45 of the other 55 — see `design-system/components/ui/
 * DetailField.tsx` for the measured census. The same defect had been
 * hand-written four more times in the counterparty pages, which is why the
 * repair went to the shared layer rather than to this file.</p>
 *
 * <p>Re-exported here so the module's own imports read unchanged. New callers
 * anywhere should import from `@/design-system/components/ui`.</p>
 */
export { DetailField, DetailFieldList, isWideFieldType } from "@/design-system/components/ui";

/** One rung. `to` names the tab that OWNS the milestone, when one does. */
export interface MilestoneItem<TTab extends string = string> {
  label: string;
  at?: string | null;
  /**
   * The tab that carries the control for this milestone.
   *
   * <p>Supplied together with {@link MilestoneListProps.onNavigate}; either one
   * without the other renders the plain row, so a caller that has not decided
   * where a rung belongs gets today's behaviour rather than a dead link.</p>
   */
  to?: TTab;
}

export interface MilestoneListProps<TTab extends string = string> {
  items: MilestoneItem<TTab>[];
  /**
   * Where to send a reader who presses a rung. Omitted on a list whose rungs
   * have no owning tab.
   */
  onNavigate?: (tab: TTab) => void;
}

/**
 * A milestone ladder.
 *
 * <p>Every milestone is listed whether or not it has happened, and an unreached
 * one says so. Rendering only the reached ones would hide what is still
 * outstanding, which is the question the ladder exists to answer.</p>
 *
 * <p>🔴 <b>Every rung is READ-ONLY, and a rung that has an owning tab is a link
 * to it.</b> Layout Standard §4.1a resolves the tension with §6.3 — "every empty
 * state carries its own quick action" — by moving the DOOR rather than by
 * exempting either rule: an Overview milestone list summarises, and the tab that
 * owns the milestone carries the control, with the full context, the permission
 * gate and the confirmation already around it. A stamp button here would be the
 * third place the same fact is written from, and it would put it on the one
 * surface with none of that context. The reader still gets there in one press.</p>
 *
 * <p>A row with no `to` renders EXACTLY as it always did, down to the markup, so
 * the lists that have no owning tab to point at are untouched.</p>
 */
export function MilestoneList<TTab extends string = string>({
  items,
  onNavigate,
}: MilestoneListProps<TTab>) {
  return (
    <ol className="grid gap-space-2">
      {items.map((m) => {
        const label = (
          <span className={m.at ? "text-small text-onyx" : "text-small text-gray-mid"}>
            {m.label}
          </span>
        );

        return (
          <li key={m.label} className="flex items-baseline gap-space-3">
            {/*
              🔴 Reached vs not reached is the pass axis of the ladder — the one
              thing this list exists to say — so it is a STATUS, not brand. Gold
              here also read as "highlighted", which is the opposite of what an
              ordinary completed milestone means. Renders on both detail pages
              that carry a ladder.
            */}
            <span
              aria-hidden
              className={
                m.at
                  ? "w-2 h-2 rounded-pill bg-status-success shrink-0"
                  : "w-2 h-2 rounded-pill border border-border-light shrink-0"
              }
            />
            {m.to && onNavigate ? (
              // A navigation, never a write — pressing it changes the tab and
              // nothing else. `text-left` because a button centres its content
              // and every other rung's label is flush.
              <button
                type="button"
                className="text-left hover:underline"
                onClick={() => onNavigate(m.to as TTab)}
              >
                {label}
              </button>
            ) : (
              label
            )}
            <span className="text-caption text-gray-mid ml-auto">
              {m.at ? <Timestamp value={m.at} /> : "Not yet"}
            </span>
          </li>
        );
      })}
    </ol>
  );
}
